// Client-side analytics utility that uses Statsig directly
import { getStatsig } from './statsigUtils';

interface AnalyticsEvent {
  event_name: string;
  event_value?: string | number;
  metadata?: Record<string, any>;
  user_id?: string;
  timestamp: string;
}

class ClientSideAnalytics {
  private eventQueue: AnalyticsEvent[] = [];
  private flushTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds

  /**
   * Track an event - same interface as statsigClient.logEvent()
   */
  async logEvent(eventName: string, eventValue?: string | number, metadata?: Record<string, any>): Promise<void> {
    try {
      const statsig = getStatsig();
      if (!statsig) {
        console.warn('Statsig not available, skipping event:', eventName);
        return;
      }

      // Log directly to Statsig client
      statsig.logEvent(eventName, eventValue, metadata);
      
      console.log('📊 Logged event to Statsig:', {
        eventName,
        eventValue,
        metadata
      });
    } catch (error) {
      console.warn('Failed to log analytics event:', error);
    }
  }

  /**
   * Force flush all pending events (useful for page unload)
   */
  async forceFlush(): Promise<void> {
    try {
      const statsig = getStatsig();
      if (statsig) {
        // Statsig client handles flushing automatically
        console.log('📤 Statsig client will handle event flushing');
      }
    } catch (error) {
      console.warn('Failed to flush events:', error);
    }
  }
}

// Create singleton instance
const clientSideAnalytics = new ClientSideAnalytics();

// Export functions that match the Statsig client interface
export const trackEventServerSide = (
  eventName: string, 
  metadata?: Record<string, any>
): void => {
  // Fire and forget - don't require callers to await
  clientSideAnalytics.logEvent(eventName, undefined, metadata).catch(error => {
    console.warn('Failed to track event:', eventName, error);
  });
};

export const trackFeatureUsageServerSide = (
  featureName: string, 
  action: string, 
  metadata?: Record<string, any>
): void => {
  // Fire and forget - don't require callers to await
  clientSideAnalytics.logEvent('feature_usage', undefined, {
    feature_name: featureName,
    action,
    ...metadata
  }).catch(error => {
    console.warn('Failed to track feature usage:', featureName, action, error);
  });
};

export const trackUserJourneyServerSide = (
  step: string, 
  metadata?: Record<string, any>
): void => {
  // Fire and forget - don't require callers to await
  clientSideAnalytics.logEvent('user_journey', undefined, {
    journey_step: step,
    ...metadata
  }).catch(error => {
    console.warn('Failed to track user journey:', step, error);
  });
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    clientSideAnalytics.forceFlush();
  });
}

export default clientSideAnalytics; 